(function () {
  'use strict';

  class HpgBandSetSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.section = this.querySelector('.hpg-band-set-selection');
      this.slides = Array.from(this.querySelectorAll('[data-hpg-band-set-slide]'));
      this.tabs = Array.from(this.querySelectorAll('[data-hpg-band-set-dot]'));
      this.stickyCta = this.querySelector('[data-hpg-band-set-sticky-cta]');
      this.stickyLink = this.querySelector('[data-hpg-band-set-sticky-link]');
      this.stickyLabel = this.querySelector('[data-hpg-band-set-sticky-label]');
      this.stickyFrameId = null;

      if (!this.section || this.slides.length === 0) return;

      this.handleClick = (event) => this.onClick(event);
      this.handleKeydown = (event) => this.onKeydown(event);
      this.handleBlockSelect = (event) => this.onBlockSelect(event);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.setupStickyCta();
      const initialIndex = this.slides.findIndex((slide) => slide.hasAttribute('aria-current'));
      this.setActive(initialIndex >= 0 ? initialIndex : 0);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.handleStickyViewportChange) {
        window.removeEventListener('scroll', this.handleStickyViewportChange);
        window.removeEventListener('resize', this.handleStickyViewportChange);
        window.visualViewport?.removeEventListener('resize', this.handleStickyViewportChange);
      }

      if (this.stickyFrameId) {
        window.cancelAnimationFrame(this.stickyFrameId);
      }
    }

    onClick(event) {
      const tab = event.target.closest('[data-hpg-band-set-dot]');
      if (!tab || !this.contains(tab)) return;

      event.preventDefault();
      const index = Number(tab.dataset.hpgBandSetDot || 0);
      this.setActive(index);
      this.focusVisibleTab(index);
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-hpg-band-set-dot]');
      if (!tab || !this.contains(tab)) return;

      const currentIndex = Number(tab.dataset.hpgBandSetDot || 0);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.slides.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.slides.length) % this.slides.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.slides.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.setActive(nextIndex);
      this.focusVisibleTab(nextIndex);
    }

    onBlockSelect(event) {
      const slide = event.target.closest?.('[data-hpg-band-set-slide]');
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.setActive(index);
    }

    focusVisibleTab(index) {
      const tab = this.tabs.find((candidate) => (
        Number(candidate.dataset.hpgBandSetDot || 0) === index
        && candidate.getClientRects().length > 0
      ));
      tab?.focus({ preventScroll: true });
    }

    setActive(index) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.activeIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.hidden = !active;
        slide.toggleAttribute('aria-current', active);
      });

      this.tabs.forEach((tab) => {
        const active = Number(tab.dataset.hpgBandSetDot || 0) === nextIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.updateStickyCta(this.slides[nextIndex]);
      this.handleStickyViewportChange?.();
    }

    setupStickyCta() {
      if (!this.stickyCta) return;

      this.stickyCta.inert = true;
      this.handleStickyViewportChange = () => {
        if (this.stickyFrameId) return;

        this.stickyFrameId = window.requestAnimationFrame(() => {
          this.stickyFrameId = null;
          this.updateStickyPosition();
        });
      };

      window.addEventListener('scroll', this.handleStickyViewportChange, { passive: true });
      window.addEventListener('resize', this.handleStickyViewportChange);
      window.visualViewport?.addEventListener('resize', this.handleStickyViewportChange);
      this.updateStickyPosition();
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    updateStickyPosition() {
      if (!this.stickyCta || !this.section) return;

      if (!this.isMobile()) {
        this.stickyCta.classList.remove('is-visible', 'is-at-end');
        this.stickyCta.setAttribute('aria-hidden', 'true');
        this.stickyCta.inert = true;
        return;
      }

      const sectionRect = this.section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const stickyHeight = this.stickyCta.offsetHeight;
      const visible = sectionRect.top <= viewportHeight - stickyHeight && sectionRect.bottom > 0;
      const atEnd = visible && sectionRect.bottom <= viewportHeight;

      this.stickyCta.classList.toggle('is-visible', visible);
      this.stickyCta.classList.toggle('is-at-end', atEnd);
      this.stickyCta.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.stickyCta.inert = !visible;
    }

    updateStickyCta(slide) {
      if (!slide || !this.stickyLink) return;

      const url = slide.dataset.hpgBandSetUrl || '';
      const label = slide.dataset.hpgBandSetCtaLabel || 'Choose This Set';

      if (this.stickyLabel) {
        this.stickyLabel.textContent = label;
      }

      if (url) {
        this.stickyLink.href = url;
        this.stickyLink.removeAttribute('aria-disabled');
      } else {
        this.stickyLink.removeAttribute('href');
        this.stickyLink.setAttribute('aria-disabled', 'true');
      }
    }
  }

  if (!customElements.get('hpg-band-set-selector')) {
    customElements.define('hpg-band-set-selector', HpgBandSetSelector);
  }
})();
